import React, { useState, useEffect } from 'react';
import SimpleBar from 'simplebar-react';

import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Slide from '@mui/material/Slide';
import Stack from '@mui/material/Stack';
import { TransitionProps } from '@mui/material/transitions';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';

import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import BtnGradient from 'src/components/common/button/BtnGradient';

import { getToken } from 'src/utils/auth';
import { getAPI } from 'src/utils/api';
import { getField } from 'src/common/helper';
import { useNotify } from 'src/context/ContextNotify';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction='left' ref={ref} {...props} />;
});

const sizeSkeleton = {
  width: '100px'
};

const fontLable = {
  fontSize: '12px',
  color: '#00000080'
};

const fontContent = {
  fontSize: '14px',
  fontWeight: 500,
  color: '#484848'
};

type DialogDetailProps = {
  open: boolean;
  patientId: number;
  id: number;
  onClose: (isRefreshList?: boolean) => void;
  onEdit: (data: any | undefined) => void;
};

const DialogDetail: React.FC<DialogDetailProps> = (props) => {
  const notifyContext = useNotify();
  const [loading, setLoading] = useState<boolean>(false);
  const [disableBtn, setDisableBtn] = useState<boolean>(false);
  const [detailReserveData, setDetailReserveData] = useState<any>();
  const [detailPatientData, setDetailPatientData] = useState<any>();

  const handleButtonClick = () => {
    const data: any = {
      ...detailReserveData,
      patientname: getField(detailPatientData, 'fullname', '')
    };

    props.onEdit(data);
  };

  const handleClose = () => {
    props.onClose();
  };

  const getDetail = async () => {
    try {
      const token = getToken();

      setLoading(true);

      if (!token) {
        throw new Error('Failed to get access token');
      }

      getAPI(`/reserve/detail?id=${props.id}&patientid=${props.patientId}&token=${token}`)
        .then((res: any) => {
          if (res.code === 200 && res.data) {
            setDetailReserveData(getField(res.data, 'reserve', {}));
            setDetailPatientData(getField(res.data, 'patient', {}));
          } else {
            setDisableBtn(true);
            notifyContext.setNotifyMessage({
              show: true,
              message: getField(res, 'msg', ''),
              type: 'error'
            });
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (props.open) {
      getDetail();
    } else {
      setDetailReserveData({});
      setDetailPatientData({});
      setDisableBtn(false);
    }
    // eslint-disable-next-line
  }, [props.open]);

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      TransitionComponent={Transition}
      sx={{
        position: 'fixed !important',
        top: '0px !important',
        right: '0px !important',
        left: 'auto',
        bottom: 'auto',
        p: '0px',
        // zIndex: 2147483647,
        '& .MuiDialog-paper': {
          m: '0px !important',
          width: '302px',
          height: '100vh',
          bgcolor: '#FAFDFF',
          borderRadius: '16px 0px 0px 16px',
        },
      }}
    >
      <DialogTitle
        id='scroll-dialog-title'
        sx={{ p: '5px 24px 0 24px', mb: '20px' }}
      >
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Stack alignItems={'center'} justifyContent={'center'}>
            <Typography fontSize={'14px'} fontWeight={700} color={'#484848'}>
              予約詳細
            </Typography>
          </Stack>
          <IconButton
            aria-label='close dialog'
            edge='end'
            onClick={handleClose}
          >
            <Box
              component={'img'}
              src='/images/ic-close-blue.svg'
              alt='icon eye'
              sx={{ width: '24px', height: '24px' }}
            />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          p: '24px 10px 24px 24px',
          '& .simplebar-track.simplebar-vertical .simplebar-scrollbar:before': {
            backgroundColor: '#CCD1D9',
          },
          overflow: 'hidden',
        }}
      >
        <SimpleBar
          style={{ height: 'calc(100vh - 69px)', borderWidth: '18px' }}
        >
          <Stack spacing='15px' height={'calc(100vh - 69px)'} sx={{ pr: '14px' }}>
            <Stack>
              <Typography sx={{ ...fontLable }}>
                患者番号
              </Typography>
              {loading ? (
                <Skeleton animation='wave' sx={sizeSkeleton} />
              ) : (
                <Typography sx={{ ...fontContent }}>
                  {getField(detailPatientData, 'patientno')}
                </Typography>
              )}
            </Stack>

            <Stack>
              <Typography sx={{ ...fontLable }}>
                名前
              </Typography>
              {loading ? (
                <Skeleton animation='wave' sx={sizeSkeleton} />
              ) : (
                <Typography sx={{ ...fontContent }}>
                  {getField(detailPatientData, 'fullname')}
                </Typography>
              )}
            </Stack>

            <Stack>
              <Typography sx={{ ...fontLable }}>
                生年月日
              </Typography>
              {loading ? (
                <Skeleton animation='wave' sx={sizeSkeleton} />
              ) : (
                <Typography sx={{ ...fontContent }}>
                  {getField(detailPatientData, 'dob')}
                </Typography>
              )}
            </Stack>

            <Stack>
              <Typography sx={{ ...fontLable }}>
                予約日
              </Typography>
              {loading ? (
                <Skeleton animation='wave' sx={sizeSkeleton} />
              ) : (
                <Typography sx={{ ...fontContent }}>
                  {getField(detailReserveData, 'thedate')}
                </Typography>
              )}
            </Stack>

            <Stack>
              <Typography sx={{ ...fontLable }}>
                時間
              </Typography>
              {loading ? (
                <Skeleton animation='wave' sx={sizeSkeleton} />
              ) : (
                <Typography sx={{ ...fontContent }}>
                  {getField(detailReserveData, 'starttime')} - {getField(detailReserveData, 'finishtime')}
                </Typography>
              )}
            </Stack>

            <Stack>
              <Typography sx={{ ...fontLable }}>
                来院時間
              </Typography>
              {loading ? (
                <Skeleton animation='wave' sx={sizeSkeleton} />
              ) : (
                <Typography sx={{ ...fontContent }}>
                  {getField(detailReserveData, 'visittime')}
                </Typography>
              )}
            </Stack>

            <Stack>
              <Typography sx={{ ...fontLable }}>
                医師
              </Typography>
              {loading ? (
                <Skeleton animation='wave' sx={sizeSkeleton} />
              ) : (
                <Typography sx={{ ...fontContent }}>
                  {getField(detailReserveData, 'doctor')}
                </Typography>
              )}
            </Stack>

            <Stack>
              <Typography sx={{ ...fontLable }}>
                予約タイプ
              </Typography>
              {loading ? (
                <Skeleton animation='wave' sx={sizeSkeleton} />
              ) : (
                <Typography sx={{ ...fontContent }}>
                  {getField(detailReserveData, 'reserve_type')}
                </Typography>
              )}
            </Stack>

            <Stack>
              <Typography sx={{ ...fontLable }}>
                希望施術
              </Typography>
              {loading ? (
                <Skeleton animation='wave' sx={sizeSkeleton} />
              ) : (
                <Typography sx={{ ...fontContent }}>
                  {getField(detailReserveData, 'part', []).length !== 0 ? getField(detailReserveData, 'part', []).join(', ') : '-'}
                </Typography>
              )}
            </Stack>

            <Stack>
              <Typography sx={{ ...fontLable }}>
                備考
              </Typography>
              {loading ? (
                <Skeleton animation='wave' sx={sizeSkeleton} />
              ) : (
                <Typography sx={{ ...fontContent }}>
                  {getField(detailReserveData, 'note')}
                </Typography>
              )}
            </Stack>

            <Stack>
              <Typography sx={{ ...fontLable }}>
                ステータス
              </Typography>
              {loading ? (
                <Skeleton animation='wave' sx={sizeSkeleton} />
              ) : (
                <Typography sx={{ ...fontContent }}>
                  {getField(detailReserveData, 'statusname')}
                </Typography>
              )}
            </Stack>

            <Stack>
              <Typography sx={{ ...fontLable }}>
                担当者
              </Typography>
              {loading ? (
                <Skeleton animation='wave' sx={sizeSkeleton} />
              ) : (
                <Typography sx={{ ...fontContent }}>
                  {getField(detailReserveData, 'fullname')}
                </Typography>
              )}
            </Stack>

            <Stack>
              <Typography sx={{ ...fontLable }}>
                伝達事項
              </Typography>
              {loading ? (
                <Skeleton animation='wave' sx={sizeSkeleton} />
              ) : (
                <Typography sx={{ ...fontContent }}>
                  {getField(detailReserveData, 'patient_note')}
                </Typography>
              )}
            </Stack>
          </Stack>
        </SimpleBar>
      </DialogContent>
      <DialogActions
        style={{
          justifyContent: 'right',
          padding: '16px'
        }}
      >
        <BtnOutlineGradient text='キャンセル' handleButtonClick={handleClose} disabled={loading} />
        <BtnGradient text='登録' handleButtonClick={handleButtonClick} disabled={loading || disableBtn} />
      </DialogActions>
    </Dialog>
  );
};

export default DialogDetail;
